import React, { useEffect, useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  BookOpen, Users, Mail, ChevronRight, ChevronDown, GraduationCap, Calendar, Clock,
} from "lucide-react";
import { toast } from "sonner";

function CourseCard({ course, lang }) {
  const [open, setOpen] = useState(false);
  const [students, setStudents] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStudents = () => {
    if (students) { setOpen(o => !o); return; }
    setLoading(true);
    api.get(`/teacher/courses/${course.id}/students`)
      .then(r => { setStudents(r.data.students); setOpen(true); })
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };

  const trackBadge = course.track === "fluent"
    ? <Badge className="bg-primary/15 text-primary border-primary/30">Fluent</Badge>
    : course.track === "bridge"
    ? <Badge className="bg-accent/15 text-accent border-accent/30">Bridge</Badge>
    : <Badge variant="outline">{lang === "fr" ? "Tous" : "All"}</Badge>;

  return (
    <Card data-testid={`teacher-course-${course.id}`} className="border-border">
      <CardHeader className="pb-3">
        <button
          onClick={fetchStudents}
          data-testid={`expand-course-${course.id}`}
          className="flex items-start justify-between gap-3 w-full text-left group"
        >
          <div className="flex-1">
            <CardTitle className="font-display tracking-tight text-xl mb-1 group-hover:text-accent transition-colors">
              {lang === "fr" ? course.name_fr : course.name_en}
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><GraduationCap className="w-3 h-3" />{course.grade_level}</span>
              {trackBadge}
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{course.day}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{course.time}</span>
              {course.format === "2x45" && course.day2 && (
                <span className="text-muted-foreground/70">+ {course.day2} {course.time2}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="secondary" className="text-xs">
              <Users className="w-3 h-3 mr-1" />
              {course.student_count} {lang === "fr" ? "élèves" : "students"}
            </Badge>
            {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          </div>
        </button>
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          {loading && <p className="text-sm text-muted-foreground py-4">{lang === "fr" ? "Chargement…" : "Loading…"}</p>}
          {!loading && students && students.length === 0 && (
            <p className="text-sm text-muted-foreground py-3 italic">
              {lang === "fr" ? "Aucun élève inscrit pour l'instant." : "No students enrolled yet."}
            </p>
          )}
          {!loading && students && students.length > 0 && (
            <div className="divide-y divide-border/60 border border-border rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-3 px-3 py-2 bg-secondary/40 text-[11px] uppercase tracking-wider font-bold text-muted-foreground">
                <div className="col-span-4">{lang === "fr" ? "Élève" : "Student"}</div>
                <div className="col-span-1 text-center">{lang === "fr" ? "Niveau" : "Grade"}</div>
                <div className="col-span-4">{lang === "fr" ? "Parent" : "Parent"}</div>
                <div className="col-span-3">{lang === "fr" ? "Contact" : "Contact"}</div>
              </div>
              {students.map(s => (
                <div
                  key={s.student_id}
                  data-testid={`student-row-${s.student_id}`}
                  className="grid grid-cols-12 gap-3 px-3 py-2.5 text-sm hover:bg-secondary/30 transition-colors"
                >
                  <div className="col-span-4 font-medium">{s.first_name} {s.last_name}</div>
                  <div className="col-span-1 text-center text-muted-foreground">{s.grade}</div>
                  <div className="col-span-4 text-muted-foreground">
                    {s.parent_first_name || s.parent_last_name
                      ? `${s.parent_first_name || ""} ${s.parent_last_name || ""}`.trim()
                      : "—"}
                  </div>
                  <div className="col-span-3 truncate">
                    {s.parent_email ? (
                      <a href={`mailto:${s.parent_email}`} className="text-accent hover:underline inline-flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        <span className="truncate">{s.parent_email}</span>
                      </a>
                    ) : <span className="text-muted-foreground">—</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default function TeacherDashboard() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/teacher/my-courses")
      .then(r => setCourses(r.data))
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const totalStudents = courses.reduce((sum, c) => sum + (c.student_count || 0), 0);
  const isPreview = user?.role === "super_admin" || user?.role === "academic_admin";

  return (
    <div className="space-y-6" data-testid="teacher-dashboard">
      <div>
        <p className="overline mb-1">OFA France · {lang === "fr" ? "Espace enseignant" : "Teacher portal"}</p>
        <h1 className="font-serif text-3xl tracking-tight">
          {lang === "fr" ? "Mes cours" : "My courses"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {isPreview && (
            <span className="italic">
              {lang === "fr"
                ? "Vue admin · vous voyez tous les cours de la plateforme. "
                : "Admin view · showing every course on the platform. "}
            </span>
          )}
          {lang === "fr"
            ? `${courses.length} cours · ${totalStudents} élèves au total`
            : `${courses.length} courses · ${totalStudents} students total`}
        </p>
      </div>

      {loading && <p className="text-sm text-muted-foreground">{lang === "fr" ? "Chargement…" : "Loading…"}</p>}

      {!loading && courses.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-10 h-10 mx-auto text-muted-foreground/60 mb-3" />
            <h3 className="font-medium mb-1">
              {lang === "fr" ? "Aucun cours assigné" : "No course assigned yet"}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {lang === "fr"
                ? "Le directeur académique n'a encore lié aucun cours à votre compte. Contactez l'administration pour être assigné(e)."
                : "The academic director has not assigned any course to your account yet. Please contact administration."}
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {courses.map(c => <CourseCard key={c.id} course={c} lang={lang} />)}
      </div>
    </div>
  );
}
